import { t, type TranslationKey } from "./i18n.ts";

/**
 * Chromium-only by design. The File System Access API is not available in
 * Firefox or Safari, and the whole storage layer is built on it — so we detect
 * and explain rather than letting the user hit a broken flow (ARCHITECTURE.md §5).
 */

interface Capability {
    readonly present: boolean;
    readonly labelKey: TranslationKey;
}

function capabilities(): Capability[] {
    return [
        {
            present: "showDirectoryPicker" in window,
            labelKey: "browser.feature.fsa"
        },
        {
            present: "indexedDB" in window && window.indexedDB !== null,
            labelKey: "browser.feature.idb"
        }
    ];
}

export function missingCapabilities(): TranslationKey[] {
    return capabilities()
        .filter((c) => !c.present)
        .map((c) => c.labelKey);
}

export function isSupported(): boolean {
    return missingCapabilities().length === 0;
}

/*
 * navigator.storage.estimate() is deliberately not used.
 *
 * It reports the origin's quota for browser-managed storage — IndexedDB and
 * the Cache API — which here holds only run state. Exported content goes to
 * disk through the File System Access API and is not counted against it, so
 * the number says nothing about whether the destination drive has room.
 *
 * No web API reports free space for a directory handle, which is why §10's
 * free-space check is a warning to the user rather than a real check.
 */

/** Human-readable byte size, localised via the common.* keys. */
export function formatBytes(bytes: number): string {
    if (bytes >= 1_073_741_824) {
        return t("common.gb", { value: (bytes / 1_073_741_824).toFixed(1) });
    }
    if (bytes >= 1_048_576) {
        return t("common.mb", { value: (bytes / 1_048_576).toFixed(1) });
    }
    if (bytes >= 1024) {
        return t("common.kb", { value: (bytes / 1024).toFixed(1) });
    }
    return t("common.bytes", { value: bytes });
}
